import React, { FC, useState, useRef, useEffect } from 'react';
import Konva from 'konva';
import ReactDOM from 'react-dom';
import { Rect, Text, Group, Transformer } from 'react-konva';
import { TagInput } from './ReferenceTag.styles';

interface ReferenceTagProps {
  tag: number;
  text: string;
  isSelected: boolean;
  isTransforming: boolean;
  isEditing: boolean;
  onSelect: (tag: number) => void;
  onDoubleClick: (tag: number) => void;
  updateTag: (tag: number, x: number, y: number, text: string, width: number, height: number) => void;
  initialX: number;
  initialY: number;
  initialWidth?: number;
  initialHeight?: number;
}
const defaultWidth = 40;
const defaultHeight = 40;

const minLength = 25;

const KonvaHtml = ({ children }: { children: React.ReactNode }) => {
  const groupRef = useRef<Konva.Group>(null);
  const [html, setHtml] = useState<HTMLDivElement | null>(null);

  useEffect(() => {
    const group = groupRef.current;
    if (!group) {
      return;
    }
    const stage = group.getStage();
    if (!stage) {
      return;
    }
    const container = stage.container();
    const newHtml = document.createElement('div');
    container.appendChild(newHtml);
    setHtml(newHtml);

    const updatePosition = () => {
      if (!groupRef.current || !newHtml) {
        return;
      }
      const stage = group.getStage();
      if (!stage) {
        return;
      }
      const position = group.getAbsolutePosition();
      const scale = stage.scale();
      newHtml.style.position = 'absolute';
      newHtml.style.top = `${position.y * (scale?.y || 1)}px`;
      newHtml.style.left = `${position.x * (scale?.x || 1)}px`;
    };

    updatePosition();
    groupRef.current.getLayer()?.on('beforeDraw', updatePosition);

    return () => {
      newHtml.remove();
      group?.getLayer()?.off('beforeDraw', updatePosition);
    };
  }, []);

  return <Group ref={groupRef}>{html && ReactDOM.createPortal(children, html)}</Group>;
};

const ReferenceTag: FC<ReferenceTagProps> = ({
  tag,
  text,
  isSelected,
  isTransforming,
  isEditing,
  onSelect,
  onDoubleClick,
  updateTag,
  initialX,
  initialY,
  initialWidth,
  initialHeight,
}) => {
  const tagRef = useRef<Konva.Group>(null);
  const transformerRef = useRef<Konva.Transformer>(null);

  const [x, setX] = useState(initialX);
  const [y, setY] = useState(initialY);
  const [width, setWidth] = useState(initialWidth ?? defaultWidth);
  const [height, setHeight] = useState(initialHeight ?? defaultHeight);

  const handleDragEnd = (e: any) => {
    setX(e.target.x());
    setY(e.target.y());
    updateTag(tag, e.target.x(), e.target.y(), text, width, height);
  };

  const handleTextChange = (e: any) => {
    updateTag(tag, x, y, e.target.value, width, height);
  };

  const handleTransform = (e: any) => {
    const node = tagRef.current;
    if (node) {
      // get change in scale
      const scaleX = node.scaleX();
      const scaleY = node.scaleY();
      // then reset scaling
      node.scaleX(1);
      node.scaleY(1);
      // then
      setX(node.x());
      setY(node.y());

      const newWidth = Math.max(minLength, node.width() * scaleX);
      const newHeight = Math.max(minLength, node.height() * scaleY);

      updateTag(tag, node.x(), node.y(), text, newWidth, newHeight);

      setWidth(newWidth);
      setHeight(newHeight);
    }
  };

  useEffect(() => {
    if (isSelected && isTransforming && transformerRef.current && tagRef.current) {
      // we need to attach transformer manually
      transformerRef.current.nodes([tagRef.current]);
      transformerRef.current.getLayer()?.batchDraw();
    }
  }, [isSelected, isTransforming]);

  return (
    <>
      <Group
        draggable
        x={x}
        y={y}
        width={width}
        height={height}
        ref={tagRef}
        onDragEnd={handleDragEnd}
        onTransformEnd={handleTransform}
        onClick={() => onSelect(tag)}
        onTap={() => onSelect(tag)}
        onDblClick={() => onDoubleClick(tag)}
        onDblTap={() => onDoubleClick(tag)}
      >
        <Rect fill="white" stroke="black" width={width} height={height} />
        {isSelected && isEditing ? (
          <KonvaHtml>
            <TagInput value={text} onChange={handleTextChange} width={width} height={height} />
          </KonvaHtml>
        ) : (
          <Text
            text={text}
            width={width}
            height={height}
            align={'center'}
            verticalAlign={'middle'}
            fontStyle={'bold'}
            fontSize={18}
          />
        )}
      </Group>
      {isSelected && isTransforming ? (
        <Transformer
          ref={transformerRef}
          boundBoxFunc={(oldBox, newBox) => {
            // limit resize
            if (newBox.width < minLength || newBox.height < minLength) {
              return oldBox;
            }
            return newBox;
          }}
        />
      ) : null}
    </>
  );
};

export default ReferenceTag;
